// Synced 2x2 grid stage.
//
// Mounts up to PLAY_CAP cells, each with its own `<video>` that the
// `useGridEngine` drives directly. A per-cell promote button reopens the
// camera in single view at the current master clock instant.

import { useEffect, useRef } from "react";

import type { GridCell } from "../hooks/useGridEngine";

export interface GridStageProps {
  cells: readonly GridCell[];
  selectedIndex: number;
  /** Wall-clock per-cell timestamp (master + virtualMs; container derives). */
  perCellWallMs: number | null;
  attach(cellId: string, video: HTMLVideoElement | null): void;
  onSelectCell(index: number): void;
  onPromoteCell(cellIndex: number): void;
}

export function GridStage({
  cells,
  selectedIndex,
  perCellWallMs,
  attach,
  onSelectCell,
  onPromoteCell,
}: GridStageProps): JSX.Element {
  if (cells.length === 0) {
    return (
      <div className="rec-grid-stage">
        <div
          className="rec-gempty"
          style={{
            position: "static",
            display: "grid",
            placeItems: "center",
            height: "100%",
            color: "#5f7186",
            fontSize: 12,
          }}
        >
          Add cameras on the right, then pick an event or range.
        </div>
      </div>
    );
  }
  return (
    <div className="rec-grid-stage">
      {cells.map((cell, idx) => (
        <GridCellView
          key={cell.id}
          cell={cell}
          index={idx}
          selected={idx === selectedIndex}
          wallMs={perCellWallMs}
          attach={attach}
          onSelect={() => onSelectCell(idx)}
          onPromote={() => onPromoteCell(idx)}
        />
      ))}
    </div>
  );
}

interface GridCellViewProps {
  cell: GridCell;
  index: number;
  selected: boolean;
  wallMs: number | null;
  attach(cellId: string, video: HTMLVideoElement | null): void;
  onSelect(): void;
  onPromote(): void;
}

function GridCellView({
  cell,
  selected,
  wallMs,
  attach,
  onSelect,
  onPromote,
}: GridCellViewProps): JSX.Element {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    attach(cell.id, ref.current);
    return () => attach(cell.id, null);
  }, [attach, cell.id]);
  return (
    <div
      className={`rec-gcell${selected ? " sel" : ""}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      aria-label={`${cell.cam.name} cell`}
    >
      <video ref={ref} playsInline muted preload="auto" />
      <span className="rec-glabel">
        <span className={`rec-st${cell.cam.online ? "" : " off"}`} aria-hidden="true" />
        {cell.cam.name}
      </span>
      {wallMs !== null && (
        <span className="rec-gts">{new Date(wallMs).toLocaleTimeString()}</span>
      )}
      <button
        type="button"
        className="rec-gpromote"
        title="Open this camera in single view"
        aria-label={`Open ${cell.cam.name} in single view`}
        onClick={(e) => {
          e.stopPropagation();
          onPromote();
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 3h6v6M21 3l-7 7M9 21H3v-6M3 21l7-7" />
        </svg>
      </button>
      {cell.loading && (
        <div className="rec-gspin">
          <div className="rec-ring" />
        </div>
      )}
      {!cell.loading && !cell.hasFootage && (
        <div className="rec-gempty">No footage in this window</div>
      )}
    </div>
  );
}
